/* Uniform dispatch calendar. Personalisation never adds processing days. */
(() => {
  'use strict';

  const DAY = 86400000;
  const HOUR = 3600000;
  const MINUTE = 60000;
  const CUTOFF_HOUR = 12;
  const holidayCache = new Map();
  const berlinClock = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/Berlin', calendar: 'gregory', numberingSystem: 'latn',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23'
  });

  function berlinParts(instant) {
    const parts = {};
    for (const part of berlinClock.formatToParts(instant)) {
      if (part.type !== 'literal') parts[part.type] = Number(part.value);
    }
    return {
      day: Date.UTC(parts.year, parts.month - 1, parts.day),
      hour: parts.hour,
      minute: parts.minute
    };
  }

  // Gregorian Easter calculation (Meeus/Jones/Butcher).
  function easterSunday(year) {
    const cycle = year % 19;
    const century = Math.floor(year / 100);
    const yearInCentury = year % 100;
    const solarCorrection = Math.floor((century + 8) / 25);
    const lunarCorrection = Math.floor((century - solarCorrection + 1) / 3);
    const epact = (19 * cycle + century - Math.floor(century / 4) - lunarCorrection + 15) % 30;
    const weekdayCorrection = (32 + 2 * (century % 4) + 2 * Math.floor(yearInCentury / 4) - epact - yearInCentury % 4) % 7;
    const correction = Math.floor((cycle + 11 * epact + 22 * weekdayCorrection) / 451);
    const monthAndDay = epact + weekdayCorrection - 7 * correction + 114;
    return Date.UTC(year, Math.floor(monthAndDay / 31) - 1, monthAndDay % 31 + 1);
  }

  function holidays(year) {
    if (holidayCache.has(year)) return holidayCache.get(year);
    const easter = easterSunday(year);
    const national = new Set([
      Date.UTC(year, 0, 1), Date.UTC(year, 4, 1), Date.UTC(year, 9, 3),
      Date.UTC(year, 11, 25), Date.UTC(year, 11, 26),
      easter - 2 * DAY, easter + DAY, easter + 39 * DAY, easter + 50 * DAY
    ]);
    const dispatch = new Set([...national, easter + 60 * DAY, Date.UTC(year, 10, 1)]);
    const result = { national, dispatch };
    holidayCache.set(year, result);
    return result;
  }

  function isDispatchDay(day) {
    const date = new Date(day);
    const weekday = date.getUTCDay();
    return weekday !== 0 && !holidays(date.getUTCFullYear()).dispatch.has(day);
  }

  function isDeliveryDay(day) {
    const date = new Date(day);
    return date.getUTCDay() !== 0 && !holidays(date.getUTCFullYear()).national.has(day);
  }

  function addOpenDays(day, count, isOpen) {
    while (count > 0) {
      day += DAY;
      if (isOpen(day)) count -= 1;
    }
    return day;
  }

  function cutoffInstant(day) {
    const utcClock = day + CUTOFF_HOUR * HOUR;
    const local = berlinParts(new Date(utcClock));
    const offset = local.day + local.hour * HOUR + local.minute * MINUTE - utcClock;
    // At noon, Berlin's daylight-saving transition is always complete.
    return utcClock - offset;
  }

  /**
   * Dates are UTC-midnight calendar labels; format them with timeZone: 'UTC'.
   * Orders before noon dispatch the following day; later orders use the next cutoff.
   * Dispatch rolls forward across Sundays and NRW holidays. Transit adds 1–2 days.
   * Manufacturing is always within 24 hours, independently of the dispatch calendar.
   */
  function estimate({ now = new Date() } = {}) {
    if (!(now instanceof Date) || !Number.isFinite(now.getTime())) {
      throw new TypeError('now must be a valid Date');
    }
    // Keep arithmetic inside the supported Gregorian calendar and Date range.
    if (now.getUTCFullYear() < 1970 || now.getUTCFullYear() > 9998) {
      throw new RangeError('now must be between the years 1970 and 9998');
    }
    const local = berlinParts(now);
    const cutoffDay = local.day + (local.hour >= CUTOFF_HOUR ? DAY : 0);
    let dispatchDay = cutoffDay + DAY;
    while (!isDispatchDay(dispatchDay)) dispatchDay += DAY;
    const remainingMilliseconds = cutoffInstant(cutoffDay) - now.getTime();
    return {
      dispatchDate: new Date(dispatchDay),
      deliveryMin: new Date(addOpenDays(dispatchDay, 1, isDeliveryDay)),
      deliveryMax: new Date(addOpenDays(dispatchDay, 2, isDeliveryDay)),
      cutoffDate: new Date(cutoffDay),
      remainingMinutes: Math.ceil(remainingMilliseconds / MINUTE),
      remainingSeconds: Math.ceil(remainingMilliseconds / 1000)
    };
  }

  globalThis.ElternAnkerDelivery = Object.freeze({ estimate });
})();
